/**
 * Root `QueryClient` factory.
 *
 * Tuned per `04-data-layer.md` §"`src/lib/query-client.ts`":
 *   - `staleTime: 30_000`   — nothing inside the dashboard changes
 *                             faster than this without a mutation
 *                             having just invalidated it. Cuts the
 *                             number of refetches on focus + on
 *                             re-mount for the shell's common
 *                             click-paths.
 *   - `gcTime: 5 * 60_000`  — keep data around for five minutes after
 *                             the last consumer unmounts. Makes back-
 *                             navigation feel free without retaining
 *                             forever (the sign-out clear clears
 *                             everything anyway).
 *   - `retry` — retry transient 5xx (count < 2) AND 429 (count < 2
 *               at the server's `Retry-After` delay). 4xx other than
 *               429 is the caller's fault; retrying would hide bugs
 *               and waste quota. 429 specifically gets a retry
 *               because a dashboard bootstrap on a suspended org
 *               (3/s sustained, 30 burst) can transiently clip a
 *               GET or two even on well-behaved use; forcing a
 *               full-page error for a transient clip is the wrong
 *               trade-off (see `_app/index.tsx` comment on
 *               `firstError`).
 *   - `refetchOnWindowFocus: false` — dashboards aren't collaborative;
 *               auto-refetching on focus creates jitter + spend with
 *               no benefit.
 *   - `mutations.retry: false` — mutations are never safe to silently
 *               retry (no idempotency key most places, plus the user
 *               can see and re-click).
 *
 * Plus `QueryCache` / `MutationCache` error handlers that implement
 * the error-surface contract from §"Errors + toasts":
 *   - 401 → `Clerk.signOut()` then `window.location.assign('/sign-in')`.
 *   - 402 → open the billing upsell store.
 *   - 403/404/5xx → `toastFromError` (with copy-request-id action).
 *   - `AbortError` / cancelled queries → silent.
 *
 * The `onError` handler is wired once here so every hook is relieved
 * of writing boilerplate `onError` for common paths. Individual hooks
 * can add an `onError` when they want mutation-specific behaviour
 * (e.g. reset a form) — it runs *in addition to* the cache-level
 * handler, not instead of it.
 */
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { FetchError } from './api';
import { toastFromError } from './errors';
import { billingPrompt } from './stores/billing-prompt';

function isAbort(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && err.name === 'AbortError') return true;
  return false;
}

function handle401Once(): void {
  if (typeof window === 'undefined') return;
  // Guard against a burst of parallel 401s (e.g. five queries firing
  // off during a stale session) all calling signOut + navigate.
  const w = window as unknown as {
    __auth_redirecting?: boolean;
    Clerk?: { signOut?: () => Promise<void> };
  };
  if (w.__auth_redirecting) return;
  w.__auth_redirecting = true;
  const target = `/sign-in?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
  const finish = (): void => {
    // `window.location.assign` forces a full reload which is exactly
    // what we want after sign-out: clears every in-memory store, zero
    // risk of a stale React tree rendering authenticated-only
    // content.
    window.location.assign(target);
  };
  const signOut = w.Clerk?.signOut;
  if (typeof signOut === 'function') {
    signOut().finally(finish);
  } else {
    finish();
  }
}

function routeError(err: unknown): void {
  if (isAbort(err)) return;

  if (err instanceof FetchError) {
    if (err.status === 401) {
      handle401Once();
      return;
    }
    if (err.status === 402) {
      billingPrompt.open({
        feature: typeof err.meta?.feature === 'string' ? err.meta.feature : null,
        meta: err.meta ?? null,
      });
      return;
    }
    if (err.status === 429) {
      // 429 is auto-retried at the query level (see `retry` below).
      // Surfacing a toast on every 429 would double-report — the
      // retry usually succeeds and the user sees nothing. If the
      // retry also fails, react-query reports the final error via
      // its normal flow and this handler fires once on the terminal
      // attempt; we surface a toast then.
      //
      // `failureCount` isn't on the cache-level error payload, so
      // we read the error's `retry_after_seconds` and trust the
      // retry config to have spent its attempts. If the tail
      // query still 429s after three tries, the bucket is
      // genuinely drained and the user should see the toast.
      toastFromError(err);
      return;
    }
    // 403 / 404 / 409 / 422 / 500 all fall through to the toast.
    // 422 is mutation-land and every form calls `toastFromError`
    // from its own `onError` anyway, but a cache-level toast is a
    // safety net for queries that somehow surface a 422.
    toastFromError(err);
    return;
  }

  // Unknown error shape — still surface so nothing silently vanishes.
  toastFromError(err);
}

/**
 * Build a fresh `QueryClient`. Called once below for the app-wide
 * singleton. Also exported directly so tests can create an isolated
 * client with the same config.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (count, err) => {
          if (err instanceof FetchError) {
            // Retry transient 5xx (not mutations — mutations set
            // retry:false below).
            if (err.status >= 500 && err.status < 600) return count < 2;
            // Retry 429 with a short cap. A dashboard bootstrap on
            // a suspended org can fire ~12 parallel GETs; even with
            // the raised suspended budget (burst=30) a bursty page
            // load landing on a partially-drained bucket can clip
            // one or two requests. Retrying those at the server's
            // suggested delay is the right move — otherwise the
            // composite Home view errors out on a single 429.
            if (err.status === 429) return count < 2;
            return false;
          }
          return false;
        },
        retryDelay: (attempt, err) => {
          // Honour `Retry-After` when the server provided it.
          // `FetchError.meta.retry_after_seconds` comes from the
          // worker-kit error envelope (see `errors.rateLimited()`).
          if (err instanceof FetchError && err.status === 429) {
            const raw = err.meta?.retry_after_seconds;
            const seconds = typeof raw === 'number' && raw > 0 ? raw : 1;
            // Cap at 5s — if the bucket is that drained the user
            // is better off seeing a toast than hanging.
            return Math.min(seconds, 5) * 1000;
          }
          // Default exponential backoff for 5xx: 250ms, 500ms.
          return Math.min(250 * 2 ** attempt, 1_000);
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (err) => routeError(err),
    }),
    mutationCache: new MutationCache({
      onError: (err) => routeError(err),
    }),
  });
}

/**
 * The app-wide singleton. Lives here — not `main.tsx` — because both
 * the entry (router context for loaders) and `routes/__root.tsx`
 * (QueryClientProvider) need it, and importing it from `@/main` made
 * the entry and the route tree circular. Rollup dedupes that cycle in
 * prod bundles, but Vite dev re-executes the entry under a `?t=` URL
 * during HMR invalidation, double-booting React (duplicate
 * `createRoot` on #root → dead event delegation until hard reload).
 */
export const queryClient = createQueryClient();
